import ConsultationForm from '@/components/common/ConsultationForm';

/**
 * InlineConsultationCTA — A polished, on-page lead-capture block that wraps
 * the canonical <ConsultationForm /> in a Study Abroad–themed section.
 *
 * Designed to slot in just before the bottom "Ready to ..." marketing CTA so
 * visitors who read the full guide can convert without leaving the page.
 *
 * Props:
 *   eyebrow      — small uppercase pre-headline (e.g. "Free 1-on-1 Counselling")
 *   headline     — main offer line (e.g. "Talk to a Germany Visa Expert")
 *   subheadline  — single sentence reassurance under the headline
 *   bullets      — array of strings ("60-second response on WhatsApp", etc.)
 *   sourceTag    — optional CRM source tag forwarded to ConsultationForm (already
 *                  handled inside ConsultationForm via window.location.pathname)
 *   testId       — data-testid for the outer section
 */
const InlineConsultationCTA = ({
  eyebrow = 'Free Expert Counselling',
  headline = 'Talk to an Expert — Free 1-on-1 Counselling',
  subheadline =
    'Share your profile and a senior counsellor will reach out within 60 seconds on WhatsApp. No spam, no obligation.',
  bullets = [
    'MEA-approved · RC: B-0762/DEL/PER/1000+/5/9148/2015',
    '10,000+ students & professionals placed since 2015',
    'Free profile review before any commitment',
  ],
  testId = 'inline-consultation-cta',
}) => {
  return (
    <section
      className="py-14 lg:py-20 bg-white"
      data-testid={testId}
      aria-labelledby="inline-consultation-cta-heading"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* Left: persuasion copy (2 cols on desktop) */}
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold tracking-[0.18em] text-[#C5A028] uppercase mb-3">
              {eyebrow}
            </p>
            <h2
              id="inline-consultation-cta-heading"
              className="text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne text-slate-900 leading-tight mb-4"
            >
              {headline}
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mb-6">
              {subheadline}
            </p>
            <ul className="space-y-2.5">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <span
                    aria-hidden="true"
                    className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-[#C5A028] flex-shrink-0"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form (3 cols on desktop, full-width on mobile) */}
          <div className="lg:col-span-3">
            <ConsultationForm variant="inline" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default InlineConsultationCTA;
